import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockDes,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
  PaginationComponent,
  Row,
  Col,
} from "../../components/Component";
import { Badge, Button, Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import { handleGetApis } from "../../service/Api";

// Read-only list of registered customers (guest buyers without an account
// appear only under Orders).

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest accounts" },
  { value: "spent", label: "Highest spend" },
  { value: "orders", label: "Most orders" },
  { value: "last_order", label: "Recent buyers" },
];

const PAYMENT_COLOR = { paid: "success", pending: "warning", failed: "danger" };

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const formatINR = (v) => `₹${inr.format(Number(v) || 0)}`;
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const initial = (name) => (name ? name.trim().charAt(0).toUpperCase() : "?");

const CustomerList = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const [list, setList] = useState({ key: null, rows: [], total: 0, summary: null, error: "" });

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState({ id: null, data: null, error: "" });

  // Search runs after the admin stops typing.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const key = `${search}|${sort}|${page}`;

  // Each response is stored with the query it answers, so a slow old
  // response can never replace a newer one.
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ page: String(page), limit: String(ITEMS_PER_PAGE), sort });
    if (search) params.set("search", search);
    handleGetApis(`admin/customers?${params}`).then((res) => {
      if (cancelled) return;
      const ok = res?.data?.success;
      setList({
        key,
        rows: ok ? res.data.data : [],
        total: ok ? res.data.pagination.total : 0,
        summary: ok ? res.data.summary : null,
        error: ok ? "" : "Couldn't load customers. Please refresh.",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [key, page, search, sort]);

  useEffect(() => {
    if (detailId === null) return;
    let cancelled = false;
    handleGetApis(`admin/customers/${detailId}`).then((res) => {
      if (cancelled) return;
      setDetail({
        id: detailId,
        data: res?.data?.success ? res.data.data : null,
        error: res?.data?.success ? "" : "Couldn't load this customer.",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [detailId]);

  const loading = list.key !== key;
  const detailLoading = detailId !== null && detail.id !== detailId;
  const d = detail.id === detailId ? detail.data : null;

  return (
    <React.Fragment>
      <Head title="Customers" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Customers</BlockTitle>
              <BlockDes className="text-soft">
                {list.summary
                  ? `${list.summary.customers} registered • ${list.summary.buyers} with a paid order`
                  : "Registered customer accounts"}
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <Row className="g-3 align-items-end">
                <Col sm={12} md={6}>
                  <label className="form-label">Search</label>
                  <div className="form-control-wrap">
                    <div className="form-icon form-icon-right">
                      <Icon name="search" />
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name, email or phone..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <label className="form-label">Sort by</label>
                  <select
                    className="form-select form-control"
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      setPage(1);
                    }}
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Col>
              </Row>
            </div>
          </div>

          <div className="card card-bordered">
            <div className="card-inner p-0">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner color="primary" />
                  <span className="ms-2 text-soft fs-14px">Loading customers...</span>
                </div>
              ) : list.error ? (
                <div className="text-center py-5 text-danger">{list.error}</div>
              ) : list.rows.length === 0 ? (
                <div className="text-center py-5">
                  <Icon name="users" style={{ fontSize: 54, opacity: 0.4 }} />
                  <h6 className="title text-dark mt-2 mb-1">No customers found</h6>
                  <p className="text-soft fs-13px">{search ? `Nothing matches "${search}".` : "No accounts yet."}</p>
                </div>
              ) : (
                <DataTableBody>
                  <DataTableHead>
                    <DataTableRow>
                      <span>Customer</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Phone</span>
                    </DataTableRow>
                    <DataTableRow size="lg">
                      <span>City</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Paid orders</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Total spent</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Last order</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <span>View</span>
                    </DataTableRow>
                  </DataTableHead>

                  {list.rows.map((c) => (
                    <DataTableItem key={c.id}>
                      <DataTableRow>
                        <div className="user-card">
                          <div className="user-avatar sm bg-primary-dim text-primary">
                            <span>{initial(c.fullname)}</span>
                          </div>
                          <div className="user-info">
                            <span className="tb-lead">{c.fullname || "—"}</span>
                            <span className="sub-text text-muted">{c.email}</span>
                          </div>
                        </div>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="tb-sub">{c.phone || "—"}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="tb-sub">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-lead">{c.paid_orders}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-lead">{formatINR(c.total_spent)}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="tb-sub">{formatDate(c.last_order_at)}</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools text-end">
                        <Button size="xs" color="light" outline className="btn-icon" onClick={() => setDetailId(c.id)} aria-label={`View ${c.fullname}`}>
                          <Icon name="eye" />
                        </Button>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              )}
            </div>

            {!loading && list.total > ITEMS_PER_PAGE && (
              <div className="card-inner">
                <PaginationComponent itemPerPage={ITEMS_PER_PAGE} totalItems={list.total} paginate={setPage} currentPage={page} />
              </div>
            )}
          </div>
        </Block>

        <Modal isOpen={detailId !== null} toggle={() => setDetailId(null)} size="lg" centered>
          <ModalHeader toggle={() => setDetailId(null)}>Customer details</ModalHeader>
          <ModalBody>
            {detailLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : !d ? (
              <div className="text-danger">{detail.error || "Customer not found."}</div>
            ) : (
              <>
                <div className="user-card mb-4">
                  <div className="user-avatar bg-primary-dim text-primary">
                    <span>{initial(d.fullname)}</span>
                  </div>
                  <div className="user-info">
                    <h5 className="mb-0">{d.fullname}</h5>
                    <span className="sub-text">{d.email}</span>
                  </div>
                </div>

                <Row className="g-3 mb-4">
                  {[
                    ["Paid orders", d.paid_orders],
                    ["Total spent", formatINR(d.total_spent)],
                    ["Wishlist", d.wishlist_items],
                    ["Reviews", Number(d.product_reviews) + Number(d.brand_reviews)],
                  ].map(([label, value]) => (
                    <Col xs={6} md={3} key={label}>
                      <div className="border rounded p-3 text-center">
                        <div className="fs-12px text-soft">{label}</div>
                        <div className="fs-18px fw-bold">{value}</div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <Row className="g-3 mb-4 fs-13px">
                  <Col md={6}>
                    <div className="text-soft">Phone</div>
                    <div>{d.phone || "—"}</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-soft">Date of birth</div>
                    <div>{formatDate(d.date_of_birth)}</div>
                  </Col>
                  <Col md={12}>
                    <div className="text-soft">Address</div>
                    <div>
                      {[d.street_address, d.city, d.state, d.pincode, d.country].filter(Boolean).join(", ") || "—"}
                    </div>
                  </Col>
                </Row>

                <h6 className="mb-2">Orders ({d.orders.length})</h6>
                {d.orders.length === 0 ? (
                  <p className="text-soft fs-13px">No orders yet.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm fs-13px">
                      <thead className="table-light">
                        <tr>
                          <th>Order</th>
                          <th>Date</th>
                          <th className="text-center">Items</th>
                          <th>Status</th>
                          <th>Payment</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.orders.map((o) => (
                          <tr key={o.id}>
                            <td>#{o.order_ref}</td>
                            <td>{formatDate(o.created_at)}</td>
                            <td className="text-center">{o.item_count}</td>
                            <td>{o.order_status}</td>
                            <td>
                              <Badge color={PAYMENT_COLOR[o.payment_status] || "secondary"} className="badge-dim">
                                {o.payment_status === "pending" ? "Not paid" : o.payment_status}
                              </Badge>
                            </td>
                            <td className="text-end">{formatINR(o.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default CustomerList;
